<template>
  <div class="date-and-time" :class="{ closable, editable }">
    <!--  Day Picker  -->
    <div class="date-time">
      <EmbedConsole v-if="editable" mode="date" @submit="editStartDate">
        <template #trigger>
          <BaseButton
            class="edit-date-button"
            :class="{ edited: edited.start_day }"
            kind="auxiliary"
            :text="formatDay('start', value)"
          />
        </template>
      </EmbedConsole>
      <span v-else>{{ formatDay("start", value) }}</span>
      <span>∙</span>
      <!--  Start Time Picker  -->
      <EmbedConsole
        v-if="editable"
        ref="embedStartTime"
        mode="daytime"
        min-width="150px"
        no-completion-helpers
        @submit="editStart"
        @validate-custom-completion="validateCustomCompletion"
      >
        <template #trigger>
          <BaseButton
            class="edit-date-button"
            :class="{ edited: edited.start_time }"
            kind="auxiliary"
            :text="isAllDay ? 'All Day' : formatDaytime('start', value)"
          />
        </template>
        <template v-if="!isAllDay" #customCompletions="{ mouseOver, selected }">
          <li
            class="custom-completion"
            :class="{ selected: selected === 0 }"
            @click="validateCustomCompletion(0)"
            @mouseover="mouseOver(0)"
          >
            All day
          </li>
        </template>
      </EmbedConsole>
      <span v-else>{{
        isAllDay ? "All Day" : formatDaytime("start", value)
      }}</span>
      <template v-if="!isAllDay">
        <span>-</span>
        <!--  End Time Picker  -->
        <EmbedConsole
          v-if="editable"
          ref="embedEndTime"
          mode="daytime"
          min-width="150px"
          @submit="editEnd"
          @validate-custom-completion="validateCustomEnd"
        >
          <template #trigger>
            <BaseButton
              class="edit-date-button"
              :class="{ edited: edited.end_time }"
              kind="auxiliary"
              :text="formatDaytime('end', value)"
            />
          </template>
          <template #completion-helpers="props">
            <ConsoleCompletionEventEndTime
              v-if="'start_time' in value"
              :start="value.start_time"
              :end="value.end_time"
              :completion="props.completion"
            />
          </template>
          <template #customCompletions="{ mouseOver, selected }">
            <li
              class="custom-completion"
              :class="{ selected: selected === 0 }"
              @click="validateCustomEnd(0)"
              @mouseover="mouseOver(0)"
            >
              All day
            </li>
            <li
              v-if="
                !(displayEndDate || differentEndDate || isAllDay) &&
                isDifferentDayPickable
              "
              class="custom-completion"
              :class="{ selected: selected === 1 }"
              @click="validateCustomEnd(1)"
              @mouseover="mouseOver(1)"
            >
              Pick different day…
            </li>
          </template>
        </EmbedConsole>
        <span v-else>{{ formatDaytime("end", value) }}</span>
      </template>
      <template
        v-if="!compact && (displayEndDate || differentEndDate || isAllDay)"
      >
        <span>∙</span>
        <EmbedConsole
          v-if="editable"
          ref="embedEndDate"
          mode="date"
          @submit="editEndDate"
        >
          <template #trigger>
            <BaseButton
              class="edit-date-button"
              :class="{ edited: edited.end_day }"
              kind="auxiliary"
              :text="formatDay('end', value)"
            />
          </template>
        </EmbedConsole>
        <span v-else>{{ formatDay("end", value) }}</span>
      </template>
      <BaseButton
        v-if="closable"
        class="close-icon"
        kind="auxiliary"
        @click="$emit('close')"
      >
        <template #icon>
          <CloseIcon />
        </template>
      </BaseButton>
    </div>
  </div>
</template>
<script lang="ts">
import {
  addDays,
  addMinutes,
  differenceInDays,
  format,
  isBefore,
  isEqual,
  isSameDay,
  startOfDay,
  subMinutes,
} from "date-fns";
import { computed, defineComponent, PropType, Ref, ref, toRefs } from "vue";

import type { Daytime, EventTime } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import ConsoleCompletionEventEndTime from "@/components/ConsoleCompletionEventEndTime.vue";
import { getDaytime } from "@/components/dates";
import EmbedConsole from "@/components/elements/EmbedConsole.vue";
import { getDuration } from "@/event";

import CloseIcon from "./icons/CloseIcon.vue";
import * as inject from "./inject";
import { UpdateObjectQuery } from "./ParsedInput.vue";
import type { TimeEdited } from "./planner/controllers/event";

export default defineComponent({
  components: {
    BaseButton,
    ConsoleCompletionEventEndTime,
    CloseIcon,
    EmbedConsole,
  },
  props: {
    value: {
      type: Object as PropType<EventTime>,
      required: true,
    },
    closable: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    edited: {
      type: Object as PropType<TimeEdited>,
      default: () => {
        return { day: false, start: false, end: false };
      },
    },
    isDifferentDayPickable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: {
    close: () => true,
    "update:value": (_: EventTime) => true,
  },
  setup(props, { emit }) {
    const { editable, value } = toRefs(props);
    const locale = inject.locale();
    const embedStartTime: Ref<InstanceType<typeof EmbedConsole> | null> =
      ref(null);
    const embedEndTime: Ref<InstanceType<typeof EmbedConsole> | null> =
      ref(null);
    const embedEndDate: Ref<InstanceType<typeof EmbedConsole> | null> =
      ref(null);
    const formatDay = (border: "start" | "end", eventTime: EventTime) => {
      if ("start_time" in eventTime) {
        return format(
          border === "start" ? eventTime.start_time : eventTime.end_time,
          "EEE, LLL dd"
        );
      } else if ("start_date" in eventTime) {
        return format(
          border === "start" ? eventTime.start_date : eventTime.end_date,
          "EEE, LLL dd"
        );
      } else {
        return format(eventTime.date, "EEE, LLL dd");
      }
    };

    const formatDaytime = (border: "start" | "end", eventTime: EventTime) => {
      if ("start_time" in eventTime) {
        return locale.value.formatDaytime(
          border === "start"
            ? getDaytime(eventTime.start_time)
            : getDaytime(eventTime.end_time)
        );
      } else if ("start_date" in eventTime) {
        return locale.value.formatDaytime(
          border === "start"
            ? getDaytime(eventTime.start_date)
            : getDaytime(eventTime.end_date)
        );
      }
    };

    const editStart = (updateObjectQuery: UpdateObjectQuery) => {
      if (updateObjectQuery.type === "daytime") {
        const daytime = updateObjectQuery.value;
        if (isAllDay.value) {
          toggleAllDay(daytime);
        } else if ("start_time" in value.value && "end_time" in value.value) {
          const start_time = new Date(value.value.start_time);
          start_time.setHours(daytime.hours);
          start_time.setMinutes(daytime.minutes || 0);
          const duration = getDuration(
            value.value.start_time,
            value.value.end_time
          );
          emit("update:value", {
            start_time,
            end_time: addMinutes(start_time, duration),
          });
        }
      }
    };

    const editEnd = (updateObjectQuery: UpdateObjectQuery) => {
      if (updateObjectQuery.type === "daytime") {
        const daytime = updateObjectQuery.value;
        if ("start_time" in value.value && "end_time" in value.value) {
          const end_time = new Date(value.value.end_time);
          end_time.setHours(daytime.hours, daytime.minutes || 0);
          if (
            isBefore(end_time, value.value.start_time) ||
            isEqual(end_time, value.value.start_time)
          ) {
            const duration = getDuration(
              value.value.start_time,
              value.value.end_time
            );
            emit("update:value", {
              start_time: subMinutes(end_time, duration),
              end_time,
            });
          } else {
            emit("update:value", {
              start_time: value.value.start_time,
              end_time,
            });
          }
        }
      }
    };

    const editStartDate = (updateObjectQuery: UpdateObjectQuery) => {
      if (updateObjectQuery.type === "dates") {
        const dates = updateObjectQuery.value;
        if (dates.type === "date") {
          if ("start_time" in value.value && "end_time" in value.value) {
            const start_time = new Date(dates.date);
            start_time.setHours(
              value.value.start_time.getHours(),
              value.value.start_time.getMinutes()
            );
            emit("update:value", {
              start_time,
              end_time: addDays(
                value.value.end_time,
                differenceInDays(start_time, value.value.start_time)
              ),
            });
          } else if ("start_date" in value.value && "end_date" in value.value) {
            const start_date = startOfDay(dates.date);
            emit("update:value", {
              start_date: startOfDay(dates.date),
              end_date: addDays(
                value.value.end_date,
                differenceInDays(start_date, value.value.start_date)
              ),
            });
          } else if ("date" in value.value) {
            emit("update:value", { date: startOfDay(dates.date) });
          }
        }
      }
    };

    const editEndDate = (updateObjectQuery: UpdateObjectQuery) => {
      if (updateObjectQuery.type === "dates") {
        const dates = updateObjectQuery.value;
        if (dates.type === "date") {
          if ("start_time" in value.value && "end_time" in value.value) {
            const end_time = new Date(dates.date);
            end_time.setHours(
              value.value.end_time.getHours(),
              value.value.end_time.getMinutes()
            );
            emit("update:value", {
              start_time:
                value.value.start_time <= end_time
                  ? value.value.start_time
                  : addMinutes(end_time, -30),
              end_time,
            });
          } else if ("start_date" in value.value || "date" in value.value) {
            const start_date =
              "start_date" in value.value
                ? value.value.start_date
                : value.value.date;
            const end_date = startOfDay(dates.date);
            emit("update:value", {
              start_date: start_date <= end_date ? start_date : end_date,
              end_date,
            });
          }
        }
      }
    };
    const toggleAllDay = (daytime?: Daytime) => {
      if (editable.value) {
        if ("start_time" in value.value && "end_time" in value.value) {
          if (isSameDay(value.value.start_time, value.value.end_time))
            emit("update:value", { date: startOfDay(value.value.start_time) });
          else {
            emit("update:value", {
              start_date: startOfDay(value.value.start_time),
              end_date: startOfDay(value.value.end_time),
            });
          }
        } else if (
          ("start_date" in value.value && "end_date" in value.value) ||
          "date" in value.value
        ) {
          const start_time =
            "start_date" in value.value
              ? value.value.start_date
              : value.value.date;
          start_time.setHours(daytime ? daytime.hours : 10);
          start_time.setMinutes(
            daytime && daytime.minutes ? daytime.minutes : 0
          );
          emit("update:value", {
            start_time,
            end_time: addMinutes(start_time, 30),
          });
        }
      }
    };

    const isAllDay = computed(() => !("start_time" in value.value));
    const displayEndDate = ref(false);
    const showEndDate = () => {
      if (
        "start_time" in value.value &&
        "end_time" in value.value &&
        isSameDay(value.value.start_time, value.value.end_time)
      ) {
        emit("update:value", {
          start_time: value.value.start_time,
          end_time: addDays(value.value.end_time, 1),
        });
      }
      displayEndDate.value = true;
    };
    const differentEndDate = computed(
      () =>
        ("start_time" in value.value &&
          "end_time" in value.value &&
          !isSameDay(value.value.start_time, value.value.end_time)) ||
        ("start_date" in value.value &&
          "end_date" in value.value &&
          !isSameDay(value.value.start_date, value.value.end_date))
    );
    const validateCustomCompletion = (index: number) => {
      switch (index) {
        case 0:
          toggleAllDay();
          break;
        case 1:
          showEndDate();
          break;
      }
      embedStartTime.value?.close();
      embedEndTime.value?.close();
    };
    const validateCustomEnd = async (index: number) => {
      await validateCustomCompletion(index);
      if (index === 1) embedEndDate.value?.show();
    };
    return {
      differentEndDate,
      displayEndDate,
      isAllDay,
      editStartDate,
      editEnd,
      editEndDate,
      editStart,
      embedEndDate,
      embedEndTime,
      embedStartTime,
      formatDay,
      getDaytime,
      formatDaytime,
      showEndDate,
      toggleAllDay,
      validateCustomCompletion,
      validateCustomEnd,
    };
  },
});
</script>

<style scoped lang="scss">
.date-and-time {
  flex-shrink: 0;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-40);
  }

  &:hover .date-time .close-icon {
    display: inherit;
  }
  .date-time {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 4px;

    .close-icon {
      display: none;
      height: 23px;
      margin-inline-start: 8px;
      .icon {
        height: 10px;
        width: 10px;
      }
    }
  }

  &.editable {
    .date-time {
      column-gap: 0;

      .edit-date-button {
        padding: 2px 4px;
        border-radius: 3px;
        font-weight: 400;

        &.edited {
          @include themed() {
            color: t($tertiary-50);
          }
        }
      }
    }
  }
}

.custom-completion {
  padding: 4px 12px;
  cursor: default;
  display: flex;
  flex-direction: row;
  &.selected {
    border-radius: 3px;

    @include themed() {
      background: t($grey-10);
    }
  }

  &:not(.selected) {
    @include themed() {
      color: t($grey-70);
    }
  }
}
</style>
